import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL = 5000;

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState('intro'); // 'intro' | 'chat'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [initialMessage, setInitialMessage] = useState('');
  const [conversationId, setConversationId] = useState(localStorage.getItem('cresa_conv_id'));
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (conversationId) setStep('chat');
  }, [conversationId]);

  const fetchMessages = useCallback(async () => {
    if (!conversationId) return;
    try {
      const res = await axios.get(`${API}/chat/${conversationId}/messages`);
      setMessages(res.data);
    } catch (e) {
      // Conversation may have been deleted by admin
      if (e.response?.status === 404) {
        localStorage.removeItem('cresa_conv_id');
        setConversationId(null);
        setMessages([]);
        setStep('intro');
      }
    }
  }, [conversationId]);

  useEffect(() => {
    if (!isOpen || !conversationId) return;
    fetchMessages();
    const id = setInterval(fetchMessages, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [isOpen, conversationId, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleStart = async (e) => {
    e.preventDefault();
    if (!name.trim() || !initialMessage.trim()) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat/start`, {
        name: name.trim(),
        email: email.trim() || null,
        message: initialMessage.trim()
      });
      const newId = res.data.conversation_id;
      localStorage.setItem('cresa_conv_id', newId);
      localStorage.setItem('cresa_visitor_name', name.trim());
      setConversationId(newId);
      setInitialMessage('');
      setStep('chat');
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!draft.trim() || !conversationId) return;
    setSending(true);
    const text = draft.trim();
    setDraft('');
    try {
      await axios.post(`${API}/chat/send`, {
        conversation_id: conversationId,
        text,
        sender_name: localStorage.getItem('cresa_visitor_name') || name
      });
      fetchMessages();
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          data-testid="chat-toggle-button"
          className="fixed bottom-6 right-6 bg-[#4A6B48] hover:bg-[#3B5539] text-white rounded-full p-4 shadow-lg transition-all hover:scale-110 z-50"
          aria-label="Ouvrir le chat"
        >
          <MessageCircle size={24} />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-80 sm:w-96 h-[500px] bg-white rounded-2xl shadow-2xl flex flex-col z-50 border border-[#E5E3DB]" data-testid="chat-window">
          {/* Header */}
          <div className="bg-[#4A6B48] text-white p-4 rounded-t-2xl flex justify-between items-center">
            <div>
              <h3 className="font-bold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Chat CRESA ASBL</h3>
              <p className="text-xs opacity-90">Nous vous répondons rapidement</p>
            </div>
            <button onClick={() => setIsOpen(false)} className="hover:bg-white/10 rounded p-1" data-testid="chat-close-button">
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          {step === 'intro' && (
            <form onSubmit={handleStart} className="flex-1 flex flex-col p-4 gap-3" data-testid="chat-intro-form">
              <p className="text-sm text-[#5C665A]">Bonjour 👋 Comment pouvons-nous vous aider ?</p>
              <input
                type="text"
                placeholder="Votre nom *"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm"
                data-testid="chat-name-input"
              />
              <input
                type="email"
                placeholder="Email (optionnel)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm"
                data-testid="chat-email-input"
              />
              <textarea
                placeholder="Votre message *"
                required
                rows={4}
                value={initialMessage}
                onChange={(e) => setInitialMessage(e.target.value)}
                className="px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm flex-1 resize-none"
                data-testid="chat-initial-message"
              />
              <button
                type="submit"
                disabled={sending}
                className="bg-[#4A6B48] hover:bg-[#3B5539] text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                data-testid="chat-start-button"
              >
                {sending ? 'Envoi...' : 'Démarrer la conversation'}
              </button>
            </form>
          )}

          {step === 'chat' && (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
                {messages.length === 0 && (
                  <p className="text-center text-sm text-[#5C665A] mt-8">Aucun message pour l&apos;instant...</p>
                )}
                {messages.map((m) => {
                  const isAdmin = m.from_role === 'admin';
                  return (
                    <div key={m.message_id} className={`flex ${isAdmin ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-[75%] rounded-2xl px-3 py-2 ${
                        isAdmin ? 'bg-[#F9F8F6] text-[#2E332D]' : 'bg-[#4A6B48] text-white'
                      }`}>
                        {isAdmin && <p className="text-xs font-semibold mb-1 text-[#4A6B48]">{m.sender_name}</p>}
                        <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                        <p className={`text-xs mt-1 ${isAdmin ? 'text-[#5C665A]' : 'text-white/70'}`}>
                          {new Date(m.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
              <form onSubmit={handleSend} className="border-t border-[#E5E3DB] p-3 flex gap-2" data-testid="chat-send-form">
                <input
                  type="text"
                  placeholder="Votre message..."
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  className="flex-1 px-3 py-2 border border-[#E5E3DB] rounded-lg text-sm"
                  data-testid="chat-draft-input"
                />
                <button
                  type="submit"
                  disabled={sending || !draft.trim()}
                  className="bg-[#4A6B48] hover:bg-[#3B5539] text-white rounded-lg px-3 disabled:opacity-50"
                  data-testid="chat-send-button"
                >
                  <Send size={18} />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
